import React, { useState } from 'react'
import {
  AppBar,
  Container,
  Toolbar,
  Box,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha
} from '@mui/material'
import {
  Home,
  Public,
  DarkMode,
  LightMode,
  Menu as MenuIcon,
  Close
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useLangTransition } from './LanguageTransition'
import { useColorMode } from '../theme'

const Navigation: React.FC = () => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const langTx = useLangTransition()
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { mode, toggleColorMode } = useColorMode()

  const langOpen = Boolean(langAnchor)
  const isDark = mode === 'dark'
  const textPrimary = isDark ? '#f6f7ff' : 'var(--color-text-strong)'
  const drawerSurface = 'var(--color-surface-elevated)'
  const borderColor = isDark ? alpha(theme.palette.primary.main, 0.35) : 'var(--color-border)'
  const actionHover = isDark
    ? alpha(theme.palette.primary.main, 0.32)
    : alpha(theme.palette.primary.main, 0.12)

  const menuItems = [
    { label: t('home'), href: '/', icon: <Home /> },
    { label: t('services'), href: '/leistungen' },
    { label: t('references'), href: '/referenzen' },
    { label: t('contact'), href: '/kontakt' }
  ]

  const languages = [
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ta', name: 'தமிழ்', flag: '🇱🇰' }
  ]

  const handleLanguageChange = (langCode: string) => {
    console.debug('[nav] change language requested', { langCode })
    langTx.start(langCode)
    handleLangClose()
    setMobileOpen(false)
  }

  const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchor(event.currentTarget)
  }

  const handleLangClose = () => {
    setLangAnchor(null)
  }

  const handleMobileMenuToggle = () => {
    console.debug('[nav] toggle mobile menu', {
      nextState: !mobileOpen,
      viewport: { width: typeof window !== 'undefined' ? window.innerWidth : undefined }
    })
    setMobileOpen(prev => !prev)
  }

  const handleMobileMenuClose = () => {
    console.debug('[nav] close mobile menu')
    setMobileOpen(false)
  }

  const handleThemeToggle = () => {
    toggleColorMode()
    handleMobileMenuClose()
  }

  const navigationMenuLabel = t('navigationMenu', { defaultValue: 'Navigation menu' })
  const iconButtonBase = {
    width: 38,
    height: 38,
    p: 0,
    borderRadius: 1.5,
    border: `1px solid ${borderColor}`,
    backgroundColor: isDark ? alpha(theme.palette.primary.main, 0.22) : 'var(--color-surface-elevated)',
    color: isDark ? '#ffffff' : 'var(--color-text-strong)',
    transition: 'all 0.25s ease',
    boxShadow: isDark ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.08)',
    '&:hover': {
      backgroundColor: actionHover,
      color: isDark ? '#ffffff' : theme.palette.primary.main,
      transform: 'translateY(-1px)'
    },
    '&:active': {
      transform: 'scale(0.96)'
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
      outlineOffset: '2px'
    }
  } as const

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: mode === 'dark'
          ? `linear-gradient(135deg, ${alpha('#1a1a1a', 0.95)} 0%, ${alpha('#2d2d2d', 0.95)} 100%)`
          : `linear-gradient(135deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f8f9ff', 0.95)} 100%)`,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        zIndex: 1000
      }}
    >
      <Container maxWidth="lg" sx={{
        px: { xs: 1, sm: 2 },
        width: '100%',
        maxWidth: '100%'
      }}>
        <Toolbar disableGutters sx={{
          justifyContent: 'space-between',
          py: 1,
          minHeight: { xs: 56, sm: 64 },
          gap: { xs: 1, sm: 2 },
          width: '100%',
          maxWidth: '100%'
        }}>
          {/* Logo */}
          <Box component="a" href="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
            <img
              src={new URL('../assets/logos/webmix-it_logo.png', import.meta.url).href}
              alt="Webmix‑IT"
              style={{
                height: 'auto',
                width: 'auto',
                maxHeight: '36px',
                maxWidth: '200px',
                filter: mode === 'dark' ? 'brightness(1.1) contrast(1.05)' : 'none'
              }}
              className="navigation-logo"
            />
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2 }}>
            {menuItems.map((item) => (
              <Box
                key={item.label}
                component="a"
                href={item.href}
                sx={{
                  textDecoration: 'none',
                  color: mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                  fontWeight: 500,
                  fontSize: { md: '0.95rem', lg: '1rem' },
                  px: { md: 1.5, lg: 2 },
                  py: 1,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>

          {/* Tablet Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex', lg: 'none' }, alignItems: 'center', gap: 1.5 }}>
            {menuItems.slice(0, 3).map((item) => (
              <Box
                key={item.label}
                component="a"
                href={item.href}
                sx={{
                  textDecoration: 'none',
                  color: mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  px: 1.5,
                  py: 0.8,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>

          {/* Right side controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Language Menu */}
            <Tooltip title={t('language')}>
              <IconButton
                onClick={handleLangClick}
                color="inherit"
                disableRipple
                sx={iconButtonBase}
              >
                <Public />
              </IconButton>
            </Tooltip>

            {/* Theme Toggle */}
            <Tooltip title={mode === 'dark' ? t('lightMode') : t('darkMode')}>
              <IconButton
                onClick={handleThemeToggle}
                color="inherit"
                disableRipple
                sx={iconButtonBase}
              >
                {mode === 'dark' ? <LightMode /> : <DarkMode />}
              </IconButton>
            </Tooltip>

            {/* Mobile Menu Button */}
            <IconButton
              onClick={handleMobileMenuToggle}
              color="inherit"
              className="mobile-menu-button"
              sx={{
                display: { xs: 'flex', md: 'none' },
                ...iconButtonBase,
                border: `1px solid ${borderColor}`,
                backgroundColor: isDark ? alpha(theme.palette.primary.main, 0.26) : 'var(--color-primary-soft)',
                '&:hover': {
                  backgroundColor: actionHover,
                  transform: 'translateY(-1px)'
                }
              }}
              aria-label={navigationMenuLabel}
            >
              {mobileOpen ? <Close sx={{ fontSize: 24 }} /> : <MenuIcon sx={{ fontSize: 24 }} />}
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Language Menu */}
      <Menu
        anchorEl={langAnchor}
        open={langOpen}
        onClose={handleLangClose}
        PaperProps={{
          sx: {
            bgcolor: drawerSurface,
            color: textPrimary,
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
            mt: 1
          }
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            sx={{
              fontSize: '1rem',
              fontWeight: i18n.language === lang.code ? 600 : 500,
              color: textPrimary,
              bgcolor: i18n.language === lang.code ? alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12) : 'transparent',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.12)
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleMobileMenuClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          className: 'mobile-navigation-drawer',
          sx: {
            width: '80vw',
            maxWidth: 320,
            px: 2,
            py: 3,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <List sx={{ flexGrow: 1, py: 0 }}>
            {menuItems.map(item => (
              <ListItemButton
                key={item.label}
                component="a"
                href={item.href}
                onClick={handleMobileMenuClose}
                className="mobile-menu-item"
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    color: '#6c5ce7 !important',
                    '& .MuiListItemIcon-root': {
                      color: '#6c5ce7 !important'
                    },
                    '& .MuiTypography-root': {
                      color: '#6c5ce7 !important'
                    }
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                    outlineOffset: '2px'
                  }
                }}
              >
                {item.icon && (
                  <ListItemIcon className="mobile-menu-icon">
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  className="mobile-menu-text"
                />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ my: 2, borderColor }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <IconButton
              onClick={(event) => handleLangClick(event)}
              disableRipple
              sx={{
                flex: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.5 : 0.35)}`,
                backgroundColor: isDark ? alpha(theme.palette.primary.main, 0.28) : 'var(--color-primary-soft)',
                color: isDark ? '#ffffff' : theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: actionHover,
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              <Public />
            </IconButton>
            <IconButton
              onClick={handleThemeToggle}
              disableRipple
              sx={{
                flex: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.45 : 0.3)}`,
                backgroundColor: isDark ? alpha(theme.palette.secondary.main, 0.32) : alpha(theme.palette.secondary.main, 0.18),
                color: isDark ? '#ffffff' : 'var(--color-text-strong)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.8),
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  )
}

export default Navigation
